import React, { useState, useEffect, useCallback } from 'react';
import { Search, ChevronLeft, ChevronRight, DollarSign, CheckCircle, Clock, XCircle, X, MapPin, Mail, Phone, Building2, Download, Plus, Upload, FileText, Globe, ArrowLeft, Package, CreditCard, CalendarDays, Hash, StickyNote, ExternalLink, Printer } from 'lucide-react';
import AdminLayout from '@/components/AdminLayout';
import { toast } from 'sonner';

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_CONFIG = {
  completed: { label: 'Validee', color: 'bg-emerald-100 text-emerald-700', dot: 'bg-emerald-500', ring: 'ring-emerald-500/20' },
  pending: { label: 'En attente', color: 'bg-amber-100 text-amber-700', dot: 'bg-amber-500', ring: 'ring-amber-500/20' },
  cancelled: { label: 'Annulee', color: 'bg-red-100 text-red-600', dot: 'bg-red-400', ring: 'ring-red-400/20' },
  refunded: { label: 'Remboursee', color: 'bg-violet-100 text-violet-600', dot: 'bg-violet-400', ring: 'ring-violet-400/20' },
  failed: { label: 'Echouee', color: 'bg-gray-100 text-gray-600', dot: 'bg-gray-400', ring: 'ring-gray-400/20' },
};

const SOURCE_LABELS = { woocommerce: 'WooCommerce', hors_ligne: 'Hors ligne', hello_asso: 'HelloAsso', platform: 'Plateforme' };

const formatDate = (d) => {
  if (!d) return '--';
  try { return new Intl.DateTimeFormat('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' }).format(new Date(d)); }
  catch { return d; }
};

const formatDateLong = (d) => {
  if (!d) return '--';
  try { return new Intl.DateTimeFormat('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' }).format(new Date(d)); }
  catch { return d; }
};

// ===== ORDER DETAIL VIEW =====
const OrderDetailView = ({ order, onBack, token }) => {
  const sc = STATUS_CONFIG[order.status] || STATUS_CONFIG.pending;

  const downloadReceipt = async () => {
    try {
      const res = await fetch(`${API}/api/wc-orders/${order.id}/receipt`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error('Erreur');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `attestation_RF-${new Date(order.order_date).getFullYear()}-${order.wc_order_number}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Attestation téléchargée');
    } catch { toast.error('Erreur lors du téléchargement'); }
  };

  const downloadJustificatif = async () => {
    try {
      const res = await fetch(`${API}/api/wc-orders/${order.id}/justificatif`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error('Erreur');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `justificatif_paiement_${order.wc_order_number}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Justificatif téléchargé');
    } catch { toast.error('Erreur lors du téléchargement'); }
  };

  return (
    <div className="space-y-6" data-testid="order-detail-view">
      {/* Back + Header */}
      <div className="flex items-center gap-4">
        <button onClick={onBack} className="w-10 h-10 rounded-xl bg-white border border-gray-200 flex items-center justify-center hover:bg-gray-50 transition-colors shadow-sm" data-testid="order-back-btn">
          <ArrowLeft size={18} className="text-gray-600" />
        </button>
        <div className="flex-1">
          <div className="flex items-center gap-3">
            <h1 className="text-xl font-bold text-gray-900 tracking-tight">Commande #{order.wc_order_number}</h1>
            <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold ${sc.color} ring-2 ${sc.ring}`}>
              <span className={`w-2 h-2 rounded-full ${sc.dot}`} />
              {sc.label}
            </span>
          </div>
          <p className="text-sm text-gray-400 mt-0.5">{formatDateLong(order.order_date)}</p>
        </div>
        <div className="flex items-center gap-2">
          {order.status === 'completed' && order.amount > 0 && (
            <div className="flex items-center gap-2">
              <button onClick={downloadJustificatif} className="flex items-center gap-2 px-4 py-2.5 bg-white border border-gray-200 text-gray-700 rounded-xl text-sm font-semibold hover:bg-gray-50 transition-colors shadow-sm" data-testid="order-download-justificatif">
                <FileText size={16} /> Justificatif
              </button>
              <button onClick={downloadReceipt} className="flex items-center gap-2 px-4 py-2.5 bg-[#1e3a5f] text-white rounded-xl text-sm font-semibold hover:bg-[#2a4a6f] transition-colors shadow-sm" data-testid="order-download-receipt">
                <Download size={16} /> Attestation
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left: Main info */}
        <div className="lg:col-span-2 space-y-6">
          {/* Amount Card */}
          <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden shadow-sm">
            <div className="bg-gradient-to-r from-[#1e3a5f] to-[#35AFE4] p-6">
              <p className="text-xs uppercase tracking-widest text-white/60 font-bold mb-1">Montant total</p>
              <p className="text-4xl font-black text-white tracking-tight">
                {order.amount > 0 ? `${order.amount.toLocaleString('fr-FR')} EUR` : 'Gratuit'}
              </p>
            </div>
            <div className="p-5 grid grid-cols-3 divide-x divide-gray-100">
              <div className="px-4 first:pl-0">
                <p className="text-[10px] uppercase tracking-wider text-gray-400 font-bold">Source</p>
                <div className="flex items-center gap-1.5 mt-1">
                  <Globe size={14} className="text-gray-400" />
                  <p className="text-sm font-semibold text-gray-800">{SOURCE_LABELS[order.source] || order.source}</p>
                </div>
              </div>
              <div className="px-4">
                <p className="text-[10px] uppercase tracking-wider text-gray-400 font-bold">Paiement</p>
                <div className="flex items-center gap-1.5 mt-1">
                  <CreditCard size={14} className="text-gray-400" />
                  <p className="text-sm font-semibold text-gray-800">{order.payment_method_label || order.payment_method || '--'}</p>
                </div>
              </div>
              <div className="px-4">
                <p className="text-[10px] uppercase tracking-wider text-gray-400 font-bold">Date</p>
                <div className="flex items-center gap-1.5 mt-1">
                  <CalendarDays size={14} className="text-gray-400" />
                  <p className="text-sm font-semibold text-gray-800">{formatDate(order.order_date)}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Product */}
          <div className="bg-white rounded-2xl border border-gray-100 p-5 shadow-sm">
            <h3 className="text-xs uppercase tracking-wider text-gray-400 font-bold mb-4 flex items-center gap-2">
              <Package size={14} /> Produit commande
            </h3>
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-[#1e3a5f]/10 flex items-center justify-center">
                  <Package size={18} className="text-[#1e3a5f]" />
                </div>
                <div>
                  <p className="font-semibold text-gray-900 text-sm">{order.product || 'Adhésion SFOALC'}</p>
                  <p className="text-xs text-gray-400">Ref: #{order.wc_order_number}</p>
                </div>
              </div>
              <p className="text-lg font-bold text-gray-900">{order.amount > 0 ? `${order.amount}EUR` : 'Gratuit'}</p>
            </div>
          </div>

          {/* Notes */}
          {order.notes && (
            <div className="bg-white rounded-2xl border border-gray-100 p-5 shadow-sm">
              <h3 className="text-xs uppercase tracking-wider text-gray-400 font-bold mb-3 flex items-center gap-2">
                <StickyNote size={14} /> Notes
              </h3>
              <div className="bg-amber-50 border border-amber-100 rounded-xl p-4">
                <p className="text-sm text-amber-900">{order.notes}</p>
              </div>
            </div>
          )}

          {/* Scan */}
          {order.scan_url && (
            <div className="bg-white rounded-2xl border border-gray-100 p-5 shadow-sm">
              <h3 className="text-xs uppercase tracking-wider text-gray-400 font-bold mb-3 flex items-center gap-2">
                <FileText size={14} /> Document scan
              </h3>
              <a href={`${BACKEND}${order.scan_url}`} target="_blank" rel="noreferrer"
                className="flex items-center gap-3 p-4 bg-blue-50 border border-blue-100 rounded-xl hover:bg-blue-100 transition-colors">
                <FileText size={20} className="text-blue-600" />
                <div className="flex-1">
                  <p className="text-sm font-semibold text-blue-800">Formulaire d'adhesion scanne</p>
                  <p className="text-xs text-blue-500">Cliquer pour ouvrir</p>
                </div>
                <ExternalLink size={16} className="text-blue-400" />
              </a>
            </div>
          )}
        </div>

        {/* Right: Client info */}
        <div className="space-y-6">
          <div className="bg-white rounded-2xl border border-gray-100 p-5 shadow-sm">
            <h3 className="text-xs uppercase tracking-wider text-gray-400 font-bold mb-4 flex items-center gap-2">
              <Building2 size={14} /> Client
            </h3>
            <div className="flex items-center gap-3 mb-5">
              <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-[#1e3a5f] to-[#35AFE4] flex items-center justify-center text-white font-bold text-lg shadow-md">
                {(order.customer_name || '--').split(' ').map(n => n[0]).join('').slice(0,2).toUpperCase()}
              </div>
              <div>
                <p className="font-bold text-gray-900">{order.customer_name || '--'}</p>
                <p className="text-xs text-gray-400">Client</p>
              </div>
            </div>
            <div className="space-y-3">
              {order.customer_email && (
                <div className="flex items-center gap-3 text-sm">
                  <div className="w-8 h-8 rounded-lg bg-gray-50 flex items-center justify-center flex-shrink-0">
                    <Mail size={14} className="text-gray-400" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-[10px] uppercase text-gray-400 font-bold">Email</p>
                    <p className="text-gray-700 truncate">{order.customer_email}</p>
                  </div>
                </div>
              )}
              {order.customer_phone && (
                <div className="flex items-center gap-3 text-sm">
                  <div className="w-8 h-8 rounded-lg bg-gray-50 flex items-center justify-center flex-shrink-0">
                    <Phone size={14} className="text-gray-400" />
                  </div>
                  <div>
                    <p className="text-[10px] uppercase text-gray-400 font-bold">Telephone</p>
                    <p className="text-gray-700">{order.customer_phone}</p>
                  </div>
                </div>
              )}
              {(order.billing_address || order.billing_city) && (
                <div className="flex items-start gap-3 text-sm">
                  <div className="w-8 h-8 rounded-lg bg-gray-50 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <MapPin size={14} className="text-gray-400" />
                  </div>
                  <div>
                    <p className="text-[10px] uppercase text-gray-400 font-bold">Adresse</p>
                    <p className="text-gray-700">{order.billing_address}</p>
                    <p className="text-gray-700">{[order.billing_postcode, order.billing_city].filter(Boolean).join(' ')}</p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Timeline */}
          <div className="bg-white rounded-2xl border border-gray-100 p-5 shadow-sm">
            <h3 className="text-xs uppercase tracking-wider text-gray-400 font-bold mb-4 flex items-center gap-2">
              <CalendarDays size={14} /> Chronologie
            </h3>
            <div className="space-y-4">
              <div className="flex gap-3">
                <div className="flex flex-col items-center">
                  <div className="w-3 h-3 rounded-full bg-emerald-500 ring-4 ring-emerald-500/20" />
                  <div className="w-0.5 flex-1 bg-gray-100 mt-1" />
                </div>
                <div className="pb-4">
                  <p className="text-sm font-semibold text-gray-900">Commande créée</p>
                  <p className="text-xs text-gray-400">{formatDateLong(order.order_date)}</p>
                </div>
              </div>
              {order.status === 'completed' && (
                <div className="flex gap-3">
                  <div className="flex flex-col items-center">
                    <div className="w-3 h-3 rounded-full bg-blue-500 ring-4 ring-blue-500/20" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-gray-900">Paiement confirme</p>
                    <p className="text-xs text-gray-400">{formatDate(order.completed_date || order.order_date)}</p>
                  </div>
                </div>
              )}
              {order.status === 'cancelled' && (
                <div className="flex gap-3">
                  <div className="flex flex-col items-center">
                    <div className="w-3 h-3 rounded-full bg-red-500 ring-4 ring-red-500/20" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-gray-900">Commande annulee</p>
                    <p className="text-xs text-gray-400">{formatDate(order.order_date)}</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ===== MAIN PAGE =====
const WcOrdersPage = () => {
  const [orders, setOrders] = useState([]);
  const [stats, setStats] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [sourceFilter, setSourceFilter] = useState('');
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const token = localStorage.getItem('token');

  const fetchOrders = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: 20 });
      if (statusFilter) params.append('status', statusFilter);
      if (sourceFilter) params.append('source', sourceFilter);
      if (search) params.append('search', search);
      const res = await fetch(`${API}/api/wc-orders?${params}`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      setOrders(data.orders || []);
      setTotalPages(data.pages || 1);
      setTotal(data.total || 0);
      setStats(data.stats || null);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [page, statusFilter, sourceFilter, search, token]);

  useEffect(() => { fetchOrders(); }, [fetchOrders]);

  // If an order is selected, show the detail view
  if (selectedOrder) {
    return (
      <div className="p-4 sm:p-6 max-w-[1400px] mx-auto">
        <OrderDetailView order={selectedOrder} onBack={() => setSelectedOrder(null)} token={token} />
      </div>
    );
  }

  return (
    <div className="space-y-5 p-4 sm:p-6 max-w-[1600px] mx-auto" data-testid="wc-orders-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-orange-100 flex items-center justify-center shadow-sm">
            <DollarSign className="text-orange-600" size={18} />
          </div>
          <div>
            <h1 className="text-xl font-bold text-gray-900 tracking-tight" data-testid="wc-orders-title">Commandes & Paiements</h1>
            <p className="text-xs text-gray-400 mt-0.5">WooCommerce + Hors ligne + HelloAsso</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs bg-gray-100 text-gray-500 px-3 py-1.5 rounded-full font-medium">{total} commandes</span>
          <button onClick={() => setShowCreate(true)} className="flex items-center gap-1.5 px-4 py-2 bg-[#1e3a5f] text-white text-xs font-semibold rounded-xl hover:bg-[#2a4a6f] transition-colors shadow-sm" data-testid="btn-create-offline">
            <Plus size={14} /> Adhésion hors ligne
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="wc-stats">
          <div className="bg-white rounded-xl border border-gray-100 p-4 shadow-sm">
            <div className="w-8 h-8 bg-emerald-100 text-emerald-600 rounded-lg flex items-center justify-center mb-2"><DollarSign size={14} /></div>
            <p className="text-[10px] uppercase tracking-wider text-gray-400 font-bold">CA Total</p>
            <p className="text-xl font-bold text-gray-900">{stats.total_revenue?.toLocaleString('fr-FR')}EUR</p>
          </div>
          <div className="bg-white rounded-xl border border-gray-100 p-4 shadow-sm">
            <div className="w-8 h-8 bg-blue-100 text-blue-600 rounded-lg flex items-center justify-center mb-2"><CheckCircle size={14} /></div>
            <p className="text-[10px] uppercase tracking-wider text-gray-400 font-bold">Validees</p>
            <p className="text-xl font-bold text-gray-900">{stats.total_completed}</p>
          </div>
          <div className="bg-white rounded-xl border border-gray-100 p-4 shadow-sm">
            <div className="w-8 h-8 bg-amber-100 text-amber-600 rounded-lg flex items-center justify-center mb-2"><Clock size={14} /></div>
            <p className="text-[10px] uppercase tracking-wider text-gray-400 font-bold">En attente</p>
            <p className="text-xl font-bold text-gray-900">{stats.total_pending}</p>
          </div>
          <div className="bg-white rounded-xl border border-gray-100 p-4 shadow-sm">
            <div className="w-8 h-8 bg-red-100 text-red-500 rounded-lg flex items-center justify-center mb-2"><XCircle size={14} /></div>
            <p className="text-[10px] uppercase tracking-wider text-gray-400 font-bold">Annulees</p>
            <p className="text-xl font-bold text-gray-900">{stats.total_cancelled}</p>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex items-center gap-3 flex-wrap">
        <form onSubmit={e => { e.preventDefault(); setPage(1); fetchOrders(); }} className="flex-1 relative min-w-[200px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-300" />
          <input type="text" value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Rechercher par nom, email, n commande..."
            className="w-full pl-9 pr-4 py-2.5 text-sm border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-200 focus:border-blue-400 outline-none" data-testid="wc-search" />
        </form>
        <select value={statusFilter} onChange={e => { setStatusFilter(e.target.value); setPage(1); }}
          className="px-3 py-2.5 text-sm border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-200 appearance-none bg-white" data-testid="wc-status-filter">
          <option value="">Tous les statuts</option>
          <option value="completed">Validees</option>
          <option value="pending">En attente</option>
          <option value="cancelled">Annulees</option>
        </select>
        <select value={sourceFilter} onChange={e => { setSourceFilter(e.target.value); setPage(1); }}
          className="px-3 py-2.5 text-sm border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-200 appearance-none bg-white" data-testid="wc-source-filter">
          <option value="">Toutes sources</option>
          <option value="woocommerce">WooCommerce</option>
          <option value="hors_ligne">Hors ligne</option>
          <option value="hello_asso">HelloAsso</option>
        </select>
      </div>

      {/* Orders Table */}
      <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden shadow-sm" data-testid="wc-orders-table">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50/80 border-b border-gray-100">
                {['N', 'Client', 'Produit', 'Montant', 'Statut', 'Source', 'Date'].map(h => (
                  <th key={h} className="text-left text-[10px] uppercase tracking-wider font-bold text-gray-400 px-4 py-3">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={7} className="text-center py-10 text-gray-400 text-sm">Chargement...</td></tr>
              ) : orders.length === 0 ? (
                <tr><td colSpan={7} className="text-center py-10 text-gray-400 text-sm">Aucune commande trouvée</td></tr>
              ) : (
                orders.map((order, i) => {
                  const sc = STATUS_CONFIG[order.status] || STATUS_CONFIG.pending;
                  return (
                    <tr key={order.id || i}
                      onClick={() => setSelectedOrder(order)}
                      className="border-b border-gray-50 hover:bg-blue-50/40 transition-colors cursor-pointer group"
                      data-testid={`wc-order-row-${i}`}>
                      <td className="px-4 py-3.5 text-sm font-mono font-bold text-[#1e3a5f] group-hover:text-[#35AFE4] transition-colors">#{order.wc_order_number}</td>
                      <td className="px-4 py-3.5">
                        <p className="text-sm font-semibold text-gray-800">{order.customer_name || '--'}</p>
                        {order.customer_email && <p className="text-xs text-gray-400 mt-0.5">{order.customer_email}</p>}
                      </td>
                      <td className="px-4 py-3.5 text-sm text-gray-600">{order.product}</td>
                      <td className="px-4 py-3.5 text-sm font-bold text-gray-900">{order.amount > 0 ? `${order.amount}EUR` : <span className="text-emerald-600">Gratuit</span>}</td>
                      <td className="px-4 py-3.5">
                        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-bold ${sc.color}`}>
                          <span className={`w-1.5 h-1.5 rounded-full ${sc.dot}`} />
                          {sc.label}
                        </span>
                      </td>
                      <td className="px-4 py-3.5">
                        <span className="text-xs px-2 py-0.5 rounded-full bg-gray-50 text-gray-500 font-medium">
                          {SOURCE_LABELS[order.source] || order.source}
                        </span>
                      </td>
                      <td className="px-4 py-3.5 text-sm text-gray-400">{formatDate(order.order_date)}</td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100">
            <p className="text-xs text-gray-400">Page {page} / {totalPages} ({total})</p>
            <div className="flex items-center gap-1">
              <button disabled={page <= 1} onClick={() => setPage(p => p - 1)} className="p-2 rounded-lg hover:bg-gray-100 disabled:opacity-30 transition-colors"><ChevronLeft size={16} /></button>
              <button disabled={page >= totalPages} onClick={() => setPage(p => p + 1)} className="p-2 rounded-lg hover:bg-gray-100 disabled:opacity-30 transition-colors"><ChevronRight size={16} /></button>
            </div>
          </div>
        )}
      </div>

      {/* Create Offline Order Modal */}
      {showCreate && <OfflineOrderModal onClose={() => setShowCreate(false)} onCreated={() => { setShowCreate(false); fetchOrders(); }} token={token} />}
    </div>
  );
};

const OfflineOrderModal = ({ onClose, onCreated, token }) => {
  const [form, setForm] = useState({
    customer_name: '', customer_email: '', customer_phone: '',
    customer_address: '', customer_city: '', customer_postcode: '',
    subscription_type: 'annual', payment_method: 'cheque', notes: ''
  });
  const [scanFile, setScanFile] = useState(null);
  const [saving, setSaving] = useState(false);

  const PRODUCTS = [
    { value: 'annual', label: 'Adhésion Praticien', price: '100EUR' },
    { value: 'soutien', label: 'Adhésion de Soutien', price: '150EUR' },
    { value: 'intern', label: 'Adhésion Interne', price: 'Gratuit' },
  ];

  const METHODS = [
    { value: 'cheque', label: 'Cheque' },
    { value: 'virement', label: 'Virement bancaire' },
    { value: 'especes', label: 'Especes' },
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.customer_name) return;
    setSaving(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      if (scanFile) fd.append('scan_file', scanFile);
      const res = await fetch(`${API}/api/admin/offline-orders`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: fd
      });
      if (res.ok) { toast.success('Commande créée'); onCreated(); }
      else toast.error('Erreur lors de la creation');
    } catch { toast.error('Erreur de connexion'); }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-sm" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-lg mx-4 shadow-2xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="offline-order-modal">
        <div className="flex items-center justify-between p-5 border-b border-gray-100 sticky top-0 bg-white rounded-t-2xl z-10">
          <div>
            <h3 className="text-sm font-bold text-gray-900">Nouvelle adhesion hors ligne</h3>
            <p className="text-[10px] text-gray-400">Saisie manuelle d'une adhesion par courrier</p>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-gray-100 rounded-lg"><X size={16} /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          <div>
            <label className="text-[10px] uppercase tracking-wider text-gray-400 font-bold block mb-1.5">Type d'adhesion</label>
            <div className="grid grid-cols-3 gap-2">
              {PRODUCTS.map(p => (
                <button type="button" key={p.value} onClick={() => setForm(f => ({ ...f, subscription_type: p.value }))}
                  className={`p-3 rounded-xl border text-center transition-colors ${form.subscription_type === p.value ? 'border-[#1e3a5f] bg-[#1e3a5f]/5' : 'border-gray-200 hover:border-gray-300'}`}
                  data-testid={`product-${p.value}`}>
                  <p className="text-xs font-bold text-gray-800">{p.label}</p>
                  <p className={`text-sm font-bold mt-1 ${p.value === 'intern' ? 'text-emerald-600' : 'text-gray-900'}`}>{p.price}</p>
                </button>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <label className="text-[10px] uppercase tracking-wider text-gray-400 font-bold block mb-1">Nom complet *</label>
              <input required value={form.customer_name} onChange={e => setForm(f => ({ ...f, customer_name: e.target.value }))}
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-200 outline-none" placeholder="Dr. Jean Dupont" data-testid="input-name" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-gray-400 font-bold block mb-1">Email</label>
              <input value={form.customer_email} onChange={e => setForm(f => ({ ...f, customer_email: e.target.value }))}
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-200 outline-none" placeholder="email@example.com" data-testid="input-email" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-gray-400 font-bold block mb-1">Telephone</label>
              <input value={form.customer_phone} onChange={e => setForm(f => ({ ...f, customer_phone: e.target.value }))}
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-200 outline-none" placeholder="06 12 34 56 78" data-testid="input-phone" />
            </div>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-gray-400 font-bold block mb-1.5">Mode de paiement</label>
            <div className="flex gap-2">
              {METHODS.map(m => (
                <button type="button" key={m.value} onClick={() => setForm(f => ({ ...f, payment_method: m.value }))}
                  className={`flex-1 px-3 py-2 rounded-xl border text-sm font-medium transition-colors ${form.payment_method === m.value ? 'border-[#1e3a5f] bg-[#1e3a5f]/5 text-[#1e3a5f]' : 'border-gray-200 text-gray-600 hover:border-gray-300'}`}
                  data-testid={`method-${m.value}`}>
                  {m.label}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-gray-400 font-bold block mb-1.5">Scan du formulaire</label>
            <label className="flex items-center gap-2 px-4 py-3 border-2 border-dashed border-gray-200 rounded-xl cursor-pointer hover:border-blue-300 transition-colors">
              <Upload size={16} className="text-gray-400" />
              <span className="text-sm text-gray-500">{scanFile ? scanFile.name : 'Choisir un fichier (PDF, JPG, PNG)'}</span>
              <input type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={e => setScanFile(e.target.files[0])} className="hidden" data-testid="input-scan" />
            </label>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-gray-400 font-bold block mb-1">Notes</label>
            <textarea value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} rows={2}
              className="w-full px-3 py-2 text-sm border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-200 outline-none resize-none" placeholder="N de cheque, remarques..." data-testid="input-notes" />
          </div>
          <button type="submit" disabled={saving || !form.customer_name}
            className="w-full py-2.5 bg-[#1e3a5f] text-white text-sm font-bold rounded-xl hover:bg-[#2a4a6f] disabled:opacity-50 transition-colors" data-testid="btn-submit-order">
            {saving ? 'Création...' : 'Créer la commande'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default WcOrdersPage;
